import React from 'react';
import { LegalLayout } from './LegalLayout';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface DatenschutzProps {
  onBack: () => void;
}

export function Datenschutz({ onBack }: DatenschutzProps) {
  const { language } = useLanguage();

  if (language === 'en') {
    return (
      <LegalLayout title="Privacy Policy" onBack={onBack}>
        <div className="space-y-8 text-slate-700">
          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">1. Data Protection at a Glance</h3>
            <h4 className="font-semibold text-slate-900 mb-2">General Information</h4>
            <p className="mb-4">
              The following notes provide a simple overview of what happens to your personal data when you visit this website. Personal data is all data with which you can be personally identified. Detailed information on the subject of data protection can be found in our privacy policy listed below this text.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">2. Responsible Body</h3>
            <p className="mb-2">The responsible body for data processing on this website is:</p>
            <p className="font-bold">Build Informed GmbH</p>
            <p>Egger-Lienz-Straße 130</p>
            <p>6020 Innsbruck</p>
            <p>Austria</p>
            <p className="mt-2">
              Email: <a href="mailto:info@buildinformed.com" className="text-purple-600 hover:underline">info@buildinformed.com</a><br />
              Tel: +43 677 61 25 85 75
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">3. Collection and Storage of Personal Data as well as Nature and Purpose of their Use</h3>
            <h4 className="font-semibold text-slate-900 mb-2">When visiting the website</h4>
            <p className="mb-4">
              When you call up our website, the browser used on your end device automatically sends information to the server of our website. This information is temporarily stored in a so-called log file. The following information is collected without your intervention and stored until automated deletion:
            </p>
            <ul className="list-disc pl-5 mb-4 space-y-1">
              <li>IP address of the requesting computer,</li>
              <li>Date and time of access,</li>
              <li>Name and URL of the retrieved file,</li>
              <li>Website from which access is made (referrer URL),</li>
              <li>Browser used and, if applicable, the operating system of your computer as well as the name of your access provider.</li>
            </ul>
            <p className="mb-4">
              The data mentioned are processed by us for the following purposes:
            </p>
            <ul className="list-disc pl-5 mb-4 space-y-1">
              <li>Ensuring a smooth connection setup of the website,</li>
              <li>Ensuring comfortable use of our website,</li>
              <li>Evaluation of system security and stability as well as</li>
              <li>for further administrative purposes.</li>
            </ul>
            <p>
              The legal basis for data processing is Art. 6 Para. 1 S. 1 lit. f GDPR. Our legitimate interest follows from the purposes for data collection listed above. In no case do we use the collected data for the purpose of drawing conclusions about your person.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">4. Disclosure of Data</h3>
            <p className="mb-4">
              A transfer of your personal data to third parties for purposes other than those listed below does not take place. We only pass on your personal data to third parties if:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You have given your express consent pursuant to Art. 6 Para. 1 S. 1 lit. a GDPR,</li>
              <li>the disclosure pursuant to Art. 6 Para. 1 S. 1 lit. f GDPR is necessary for the assertion, exercise or defense of legal claims and there is no reason to assume that you have an overriding interest worthy of protection in the non-disclosure of your data,</li>
              <li>in the event that a legal obligation exists for the transfer pursuant to Art. 6 Para. 1 S. 1 lit. c GDPR, as well as</li>
              <li>this is legally permissible and required according to Art. 6 Para. 1 S. 1 lit. b GDPR for the processing of contractual relationships with you.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">5. Authentication and Third Party Services</h3>
            <h4 className="font-semibold text-slate-900 mb-2">Clerk (Authentication)</h4>
            <p className="mb-4">
              We use the service "Clerk", provided by Clerk, Inc., 165 11th St, San Francisco, CA 94103, USA, for authentication and user account management. If you register or log in to our service, the data you enter (e.g. email address, password, name) will be transmitted to Clerk and stored there. Further information on data protection at Clerk can be found at: <a href="https://clerk.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">https://clerk.com/privacy</a>.
            </p>

            <h4 className="font-semibold text-slate-900 mb-2">Sentry (Error Analysis)</h4>
            <p className="mb-4">
              To improve the stability and troubleshooting of our application, we use the service "Sentry" from Functional Software, Inc., 132 Hawthorne Street, San Francisco, CA 94107, USA. Sentry is used to log software crashes and errors. Technical data (e.g. operating system, browser version, error messages) is transmitted to Sentry servers in the USA. We have configured Sentry so that IP addresses are masked and no personal data is transmitted in plain text, as far as technically possible. Further information: <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">https://sentry.io/privacy/</a>.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">6. Rights of Data Subjects</h3>
            <p className="mb-4">You have the right:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>to request information about your personal data processed by us in accordance with Art. 15 GDPR;</li>
              <li>to request the correction of incorrect or completion of your personal data stored by us immediately in accordance with Art. 16 GDPR;</li>
              <li>to request the deletion of your personal data stored by us in accordance with Art. 17 GDPR;</li>
              <li>to request the restriction of processing of your personal data in accordance with Art. 18 GDPR;</li>
              <li>to receive your personal data that you have provided to us in a structured, common and machine-readable format or to request the transfer to another person responsible in accordance with Art. 20 GDPR;</li>
              <li>to revoke your consent once given to us at any time in accordance with Art. 7 Para. 3 GDPR;</li>
              <li>to complain to a supervisory authority in accordance with Art. 77 GDPR.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">7. Data Security</h3>
            <p>
              We use the widespread SSL procedure (Secure Socket Layer) in connection with the highest encryption level supported by your browser during the website visit. You can tell whether a single page of our website is transmitted in encrypted form by the closed representation of the key or lock symbol in the status bar of your browser. We also use appropriate technical and organizational security measures to protect your data against accidental or intentional manipulation, partial or complete loss, destruction or against unauthorized access by third parties.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">8. Topicality and Modification of this Privacy Policy</h3>
            <p>
              This privacy policy is currently valid and has the status January 2026. Due to the further development of our website and offers thereof or due to changed legal or official requirements, it may become necessary to change this privacy policy.
            </p>
          </section>
        </div>
      </LegalLayout>
    );
  }

  return (
    <LegalLayout title="Datenschutzerklärung" onBack={onBack}>
      <div className="space-y-8 text-slate-700">
        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">1. Datenschutz auf einen Blick</h3>
          <h4 className="font-semibold text-slate-900 mb-2">Allgemeine Hinweise</h4>
          <p className="mb-4">
            Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">2. Verantwortliche Stelle</h3>
          <p className="mb-2">Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:</p>
          <p className="font-bold">Build Informed GmbH</p>
          <p>Egger-Lienz-Straße 130</p>
          <p>6020 Innsbruck</p>
          <p>Österreich</p>
          <p className="mt-2">
            E-Mail: <a href="mailto:info@buildinformed.com" className="text-purple-600 hover:underline">info@buildinformed.com</a><br />
            Tel: +43 677 61 25 85 75
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">3. Erhebung und Speicherung personenbezogener Daten sowie Art und Zweck von deren Verwendung</h3>
          <h4 className="font-semibold text-slate-900 mb-2">Beim Besuch der Website</h4>
          <p className="mb-4">
            Beim Aufrufen unserer Website werden durch den auf Ihrem Endgerät zum Einsatz kommenden Browser automatisch Informationen an den Server unserer Website gesendet. Diese Informationen werden temporär in einem sog. Logfile gespeichert. Folgende Informationen werden dabei ohne Ihr Zutun erfasst und bis zur automatisierten Löschung gespeichert:
          </p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>IP-Adresse des anfragenden Rechners,</li>
            <li>Datum und Uhrzeit des Zugriffs,</li>
            <li>Name und URL der abgerufenen Datei,</li>
            <li>Website, von der aus der Zugriff erfolgt (Referrer-URL),</li>
            <li>verwendeter Browser und ggf. das Betriebssystem Ihres Rechners sowie der Name Ihres Access-Providers.</li>
          </ul>
          <p className="mb-4">
            Die genannten Daten werden durch uns zu folgenden Zwecken verarbeitet:
          </p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Gewährleistung eines reibungslosen Verbindungsaufbaus der Website,</li>
            <li>Gewährleistung einer komfortablen Nutzung unserer Website,</li>
            <li>Auswertung der Systemsicherheit und -stabilität sowie</li>
            <li>zu weiteren administrativen Zwecken.</li>
          </ul>
          <p>
            Die Rechtsgrundlage für die Datenverarbeitung ist Art. 6 Abs. 1 S. 1 lit. f DSGVO. Unser berechtigtes Interesse folgt aus oben aufgelisteten Zwecken zur Datenerhebung. In keinem Fall verwenden wir die erhobenen Daten, um Rückschlüsse auf Ihre Person zu ziehen.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">4. Weitergabe von Daten</h3>
          <p className="mb-4">
            Eine Übermittlung Ihrer persönlichen Daten an Dritte zu anderen als den im Folgenden aufgeführten Zwecken findet nicht statt. Wir geben Ihre persönlichen Daten nur an Dritte weiter, wenn:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Sie Ihre nach Art. 6 Abs. 1 S. 1 lit. a DSGVO ausdrückliche Einwilligung dazu erteilt haben,</li>
            <li>die Weitergabe nach Art. 6 Abs. 1 S. 1 lit. f DSGVO zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist und kein Grund zur Annahme besteht, dass Sie ein überwiegendes schutzwürdiges Interesse an der Nichtweitergabe Ihrer Daten haben,</li>
            <li>für den Fall, dass für die Weitergabe nach Art. 6 Abs. 1 S. 1 lit. c DSGVO eine gesetzliche Verpflichtung besteht, sowie</li>
            <li>dies gesetzlich zulässig und nach Art. 6 Abs. 1 S. 1 lit. b DSGVO für die Abwicklung von Vertragsverhältnissen mit Ihnen erforderlich ist.</li>
          </ul>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">5. Authentifizierung und Dienste Dritter</h3>
          <h4 className="font-semibold text-slate-900 mb-2">Clerk (Authentifizierung)</h4>
          <p className="mb-4">
            Für die Authentifizierung und Verwaltung von Benutzerkonten verwenden wir den Dienst "Clerk", bereitgestellt von Clerk, Inc., 165 11th St, San Francisco, CA 94103, USA. Wenn Sie sich bei unserem Dienst registrieren oder anmelden, werden die von Ihnen eingegebenen Daten (z.B. E-Mail-Adresse, Passwort, Name) an Clerk übermittelt und dort gespeichert. Weitere Informationen zum Datenschutz bei Clerk finden Sie unter: <a href="https://clerk.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">https://clerk.com/privacy</a>.
          </p>

          <h4 className="font-semibold text-slate-900 mb-2">Sentry (Fehleranalyse)</h4>
          <p className="mb-4">
            Zur Verbesserung der Stabilität und Fehlerbehebung unserer Anwendung nutzen wir den Dienst "Sentry" der Functional Software, Inc., 132 Hawthorne Street, San Francisco, CA 94107, USA. Sentry dient dazu, Abstürze und Fehler der Software zu protokollieren. Dabei werden technische Daten (z.B. Betriebssystem, Browserversion, Fehlermeldungen) an Server von Sentry in den USA übermittelt. Wir haben Sentry so konfiguriert, dass IP-Adressen maskiert werden und keine personenbezogenen Daten im Klartext übertragen werden, sofern dies technisch möglich ist. Weitere Informationen: <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">https://sentry.io/privacy/</a>.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">6. Betroffenenrechte</h3>
          <p className="mb-4">Sie haben das Recht:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>gemäß Art. 15 DSGVO Auskunft über Ihre von uns verarbeiteten personenbezogenen Daten zu verlangen;</li>
            <li>gemäß Art. 16 DSGVO unverzüglich die Berichtigung unrichtiger oder Vervollständigung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen;</li>
            <li>gemäß Art. 17 DSGVO die Löschung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen;</li>
            <li>gemäß Art. 18 DSGVO die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen;</li>
            <li>gemäß Art. 20 DSGVO Ihre personenbezogenen Daten, die Sie uns bereitgestellt haben, in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten oder die Übermittlung an einen anderen Verantwortlichen zu verlangen;</li>
            <li>gemäß Art. 7 Abs. 3 DSGVO Ihre einmal erteilte Einwilligung jederzeit gegenüber uns zu widerrufen;</li>
            <li>gemäß Art. 77 DSGVO sich bei einer Aufsichtsbehörde zu beschweren.</li>
          </ul>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">7. Datensicherheit</h3>
          <p>
            Wir verwenden innerhalb des Website-Besuchs das verbreitete SSL-Verfahren (Secure Socket Layer) in Verbindung mit der jeweils höchsten Verschlüsselungsstufe, die von Ihrem Browser unterstützt wird. Ob eine einzelne Seite unseres Internetauftrittes verschlüsselt übertragen wird, erkennen Sie an der geschlossenen Darstellung des Schüssel- beziehungsweise Schloss-Symbols in der Statusleiste Ihres Browsers. Wir bedienen uns im Übrigen geeigneter technischer und organisatorischer Sicherheitsmaßnahmen, um Ihre Daten gegen zufällige oder vorsätzliche Manipulationen, teilweisen oder vollständigen Verlust, Zerstörung oder gegen den unbefugten Zugriff Dritter zu schützen.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">8. Aktualität und Änderung dieser Datenschutzerklärung</h3>
          <p>
            Diese Datenschutzerklärung ist aktuell gültig und hat den Stand Januar 2026. Durch die Weiterentwicklung unserer Website und Angebote darüber oder aufgrund geänderter gesetzlicher beziehungsweise behördlicher Vorgaben kann es notwendig werden, diese Datenschutzerklärung zu ändern.
          </p>
        </section>
      </div>
    </LegalLayout>
  );
}
